import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";

import useAppInsightsHandler from "hooks/useAppInsightsHandler.js";

import { addAlarm, deleteAlarmApi, getAlarms, updateAlarm, updateAllAlarms } from "api/alarmsList.js";

import getLogger from "utils/logger.util";
import { getRowFormattedData } from "utils/utils.js";
import { extractSystemId } from "utils/utils.js";
import { AlarmPageStrings, ALARMS_TABLE_STRINGS, dialogStrings, InputErrorStrings, valueStrings } from "constants/constantStrings";
import { CHECKBOX_STATES, INPUT_MINIMUM } from "constants/globalConstants";

import AddTagAlarmModal from "components/AddTagAlarmModal";
import AgTable from "components/AgTable";
import DeleteTagAlarmModal from "components/DeleteTagAlarmModal/DeleteTagAlarmModal";
import DupontDialog from "components/DupontDialog";
import Filter from "components/Filter";
import PageLoading from "components/PageLoading";
import TableDialog from "components/TableDialog/index.js";

import { getAlarmsListTableColDef } from "./AlarmList.table.config";
import AlarmListEditForm from "./AlarmListEditForm.jsx";

import "./index.scss";

const selectedRowObject = {
  AlarmID: "",
  AlarmName: "",
  Description: "",
  SendAlert: "",
  ShowInReport: ""
};

const AlarmList = ({ setAlarmListCount, admin, systemsAsObj, showAddAlarmDialog, handleAddAlarmClose }) => {
  const gridRef = useRef();
  const [isLoading, setIsLoading] = useState(true);
  const [alarms, setAlarms] = useState([]);
  const [colDefs, setColDefs] = useState();
  const [filterValue, setFilterValue] = useState("");
  const [popupData, setPopupData] = useState();
  const [showTableFilterIcon, setShowTableFilterIcon] = useState(false);
  const [isEditAlarmListSystemModalOpen, setisEditAlarmListSystemModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(selectedRowObject);
  const systemID = extractSystemId();
  const systemName = systemID ? systemsAsObj[systemID]?.systemName : "";
  const [confirmDisable, setConfirmDisabled] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [alarmNameError, setAlarmNameError] = useState(null);
  const [alarmDescError, setAlarmDescError] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);

  const objAppInsights = useAppInsightsHandler();
  const Logger = getLogger("AlarmList");

  const fetchData = async () => {
    try {
      const response = await getAlarms();
      if (response.status >= 200 && response.status < 300) {
        const alarmList = response.data.Alarms;
        setAlarms(alarmList);
        setAlarmListCount(alarmList?.length);
        setIsLoading(false);
        updateColDefs(alarmList);
      }
    } catch (err) {
      Logger.log("Alarms error: ", err);
    }
  };
  useEffect(() => {
    fetchData();
  }, []);

  const handleUpdateAlarm = async item => {
    try {
      await updateAlarm(item);
    } catch (err) {
      Logger.err("Update Alarm error ", err);
    }
  };

  const handleAddAlarmSubmit = async formData => {
    try {
      await addAlarm(formData);
      setIsLoading(true);
      await fetchData();
      handleAddAlarmClose();
    } catch (error) {
      Logger.log("Error adding Alarm:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateAlarmsList = alarmsToUpdate => {
    alarmsToUpdate.forEach(element => {
      handleUpdateAlarm(element);
    });
    const itemsToUpdate = getTableData();
    setAlarms(itemsToUpdate);
  };

  useEffect(() => {
    updateColDefs(getTableData());
  }, [showTableFilterIcon]);

  const geColHeaderCheckboxState = (sourceList, field) => {
    let state = CHECKBOX_STATES.INDETERMINATE;
    const checkedAlarms = sourceList.filter(alarm => Boolean(alarm[field]));
    if (checkedAlarms.length === 0) {
      state = CHECKBOX_STATES.UNCHECKED;
    } else if (checkedAlarms.length === sourceList.length) {
      state = CHECKBOX_STATES.CHECKED;
    } else {
      state = CHECKBOX_STATES.INDETERMINATE;
    }
    return state;
  };

  const getGridAPI = () => gridRef?.current?.api;

  const updateColDefs = alarmList => {
    const SendAlert = geColHeaderCheckboxState(alarmList, "SendAlert");
    const ShowInReport = geColHeaderCheckboxState(alarmList, "ShowInReport");
    const defs = getAlarmsListTableColDef(showTableFilterIcon, { SendAlert, ShowInReport }, admin);
    setColDefs(defs);
  };

  const isValidForm = () => {
    const isAlarmNameValid = selectedRow.AlarmName && selectedRow.AlarmName && selectedRow.AlarmName.trim().length >= INPUT_MINIMUM;
    const isAlarmDescValid = selectedRow.Description && selectedRow.Description && selectedRow.Description.trim().length >= INPUT_MINIMUM;

    return isAlarmDescValid && isAlarmNameValid;
  };

  useEffect(() => {
    setConfirmDisabled(!isValidForm());
  }, [selectedRow]);

  const onInputChange = (value, name) => {
    if (value && value.trim().length < INPUT_MINIMUM && name === "AlarmName") {
      setAlarmNameError(InputErrorStrings.minimumRequired);
      setConfirmDisabled(true);
    } else if (value && value.trim().length < INPUT_MINIMUM && name === "Description") {
      setAlarmDescError(InputErrorStrings.minimumRequired);
      setConfirmDisabled(true);
    } else {
      setAlarmDescError(null);
      setAlarmNameError(null);
    }
    setSelectedRow({ ...selectedRow, [name]: value });
  };

  const onSendAlertChange = SendAlert => {
    setSelectedRow({ ...selectedRow, SendAlert });
  };

  const onShowReportChange = ShowInReport => {
    setSelectedRow({ ...selectedRow, ShowInReport });
  };

  const handleCloseEditAlarmForm = async data => {
    if (data === dialogStrings.confirm) {
      setIsLoading(true);
      setIsEditing(true);
      const updateAlarms = async () => {
        try {
          const response = await updateAlarm(selectedRow);
          if (response.status >= 200 && response.status < 300) {
            fetchData();
          }
        } catch (err) {
          Logger.err("Edit Alarm Form error ", err);
        } finally {
          setIsEditing(false);
          setisEditAlarmListSystemModalOpen(false);
        }
      };
      updateAlarms();
    } else {
      setisEditAlarmListSystemModalOpen(false);
    }

    setIsLoading(false);
  };

  const handleEditClick = rowData => {
    objAppInsights.trackEvent("alarmListRowEdit");
    setisEditAlarmListSystemModalOpen(true);
    setAlarmDescError(null);
    setAlarmNameError(null);
    const { AlarmID, AlarmName, Description, SendAlert, ShowInReport } = rowData;
    setSelectedRow({ AlarmID, AlarmName, Description, SendAlert, ShowInReport });
  };

  const getTableData = () => {
    const updatedTags = [];
    const gridAPI = getGridAPI();
    if (gridAPI) {
      gridAPI.forEachNode(node => updatedTags.push(node.data));
    }
    return updatedTags;
  };

  useEffect(() => updateColDefs(alarms), [alarms]);

  const onAlarmUpdate = updatedAlarm => {
    const itemsToUpdate = getTableData();
    setAlarms(itemsToUpdate);
    updateAlarm(updatedAlarm);
    //updateColDefs(itemsToUpdate);
  };

  const onColumnSelection = (colKey, value) => {
    const gridAPI = getGridAPI();
    if (gridAPI) {
      const itemsToUpdate = [];
      gridAPI.forEachNodeAfterFilterAndSort(node => {
        const { data } = node;
        data[colKey] = value;
        itemsToUpdate.push(data);
      });
      gridAPI.applyTransaction({ update: itemsToUpdate });
      if (itemsToUpdate.length === alarms.length) {
        setAlarms(itemsToUpdate);
        updateAllAlarms({ [colKey]: value });
      } else {
        handleUpdateAlarmsList(itemsToUpdate);
      }
    }
  };

  const onFilterChange = val => {
    objAppInsights.trackEvent("alarmListFilterChange");
    setFilterValue(val);
    if (!val) {
      updateColDefs(getTableData());
      fetchData();
    }
  };

  const closePopup = () => setPopupData(null);
  const onRowClicked = ({ data }) => {
    objAppInsights.trackEvent("alarmListRowClick");
    const rowData = getRowFormattedData(colDefs, data);
    const title = data.AlarmID;
    const subTitle = ALARMS_TABLE_STRINGS.viewAlarm;
    setPopupData({ title, subTitle, rowData });
  };

  const onAlarmMaxDescriptionLimit = maxlimit => {
    setAlarmDescError(InputErrorStrings.maxlimitis + maxlimit);
  };

  const onAlarmNameMaxLimit = maxlimit => {
    setAlarmNameError(InputErrorStrings.maxlimitis + maxlimit);
  };

  const handleDeleteClick = rowData => {
    objAppInsights.trackEvent("AlarmListRowDelete");
    const { AlarmID } = rowData;
    setDeleteRow({ AlarmID });
  };

  const onDeleteDlgClose = async action => {
    setDeleteRow(null);
    if (action === dialogStrings.confirm) {
      await deleteAlarm();
    }
  };

  const deleteAlarm = async () => {
    try {
      setIsLoading(true);
      await deleteAlarmApi(deleteRow.AlarmID);
      setFilterValue("");
      await fetchData();
    } catch (error) {
      Logger.log("Error deleting alarm:", error);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <>
      {isLoading ? (
        <PageLoading customHeight={"calc(100vh - 150px)"} />
      ) : (
        <div className="alarmHistoryCenter">
          <div className="flex-between marginBtm20">
            <span className="alarm-description descriptionText">
              {AlarmPageStrings.editAlarmDescription1}
              <br />
              <u>{AlarmPageStrings.editAlarmDescription2}</u>
            </span>
            <div className="jc-end">
              <Filter
                data={alarms}
                placeholder={AlarmPageStrings.searchHere}
                setFilterValue={onFilterChange}
                setShowTableFilterIcon={setShowTableFilterIcon}
                showTableFilterIcon={showTableFilterIcon}
                customClass="marginBtm10"
              />
            </div>
          </div>
          <AgTable
            gridRef={gridRef}
            rowData={alarms}
            onRowClicked={onRowClicked}
            quickFilterText={filterValue}
            columnDefs={colDefs}
            context={{ onAlarmUpdate, onColumnSelection, handleEditClick, handleDeleteClick }}
            editType="fullRow"
            setBadgeCount={setAlarmListCount}
            customHeight="calc(100vh - 215px)"
          />
          {isEditAlarmListSystemModalOpen && (
            <DupontDialog
              open={isEditAlarmListSystemModalOpen}
              title={AlarmPageStrings.editAlarm}
              subTitle={AlarmPageStrings.editAlarmDescription.replace("[%]", systemName)}
              showisRequired={true}
              cancelBtnText={valueStrings.cancel}
              confirmBtnText={valueStrings.save}
              onClose={handleCloseEditAlarmForm}
              confirmDisabled={confirmDisable}
              isOverlayLoader={isEditing}
            >
              <AlarmListEditForm
                selectedAlarmDetails={selectedRow}
                onInputChange={onInputChange}
                onSendAlertChange={onSendAlertChange}
                onShowReportChange={onShowReportChange}
                alarmNameError={alarmNameError}
                alarmDescError={alarmDescError}
                onAlarmMaxDescriptionLimit={onAlarmMaxDescriptionLimit}
                onAlarmNameMaxLimit={onAlarmNameMaxLimit}
              />
            </DupontDialog>
          )}
          {showAddAlarmDialog && (
            <AddTagAlarmModal open isAddAlarm Tags={alarms} handleClose={handleAddAlarmClose} onFormSubmit={handleAddAlarmSubmit} />
          )}
          {deleteRow && <DeleteTagAlarmModal isDeleteAlarm deleteRow={deleteRow} onDeleteDlgClose={onDeleteDlgClose} />}
          {popupData && <TableDialog popupData={popupData} closePopup={closePopup} />}
        </div>
      )}
    </>
  );
};

const mapStateToProps = ({ user, systems }) => ({
  admin: user.isAdmin,
  systemsAsObj: systems.systemsAsObj
});

export default connect(mapStateToProps, null)(AlarmList);
